/* eslint-disable import/no-duplicates */

import { useEffect, useState } from 'react';
import type React from 'react';

import { useAppContext } from '../../../components/AppContext';
import type { Flow, FriendlyError } from '../../../modules/Auth0/types';
import { recaptchaNonResetErrors } from '../../../modules/Auth0/utils/mapError';
import { useAnalyticsContext } from '../../../modules/analytics/AnalyticsContext';
import type { BotState, FormState } from '../../../types';
import { handleCloudflareBotCheck } from '../../../utils/handleCloudflareBotCheck';
import { handleRecaptchaRender } from '../../../utils/handleRecaptchaRender';

import { useRecaptchaContext } from './RecaptchaContext';
import isChallengedByCloudFlare from './isChallengedByCloudFlare';
import renderCaptchaCheckbox from './renderCaptchaCheckbox';
import { resetCaptchaToken } from './resetCaptchaToken';
import { isNonResetRecaptchaError, shouldRenderRecaptcha } from './utils';

interface Props {
  setFormState: React.Dispatch<React.SetStateAction<FormState<any>>>;
  formError: FriendlyError | undefined;
  formLoading?: boolean;
  formSubmitted?: boolean;
  setBotState: React.Dispatch<React.SetStateAction<BotState>>;
  isBotCheckDone?: boolean;
  isRenderRecaptchaStarted?: boolean;
  isRenderRecaptchaDone?: boolean;
  flow: Flow;
  showRecaptcha: boolean;
}

const resetRecaptcha = async (flow: Flow, googleCaptchaSiteKey: string) => {
  await resetCaptchaToken(flow, googleCaptchaSiteKey);
};

const ReCaptcha = ({
  formError,
  formLoading,
  formSubmitted,
  setFormState,
  isBotCheckDone,
  isRenderRecaptchaStarted,
  isRenderRecaptchaDone,
  setBotState,
  flow,
  showRecaptcha,
}: Props) => {
  const { googleCaptchaSiteKey } = useAppContext();
  const { analytics } = useAnalyticsContext();
  const { recaptchaScriptLoaded } = useRecaptchaContext();
  const [isBotCheckStarted, setIsBotCheckStarted] = useState(false);
  const [isRecaptchaRequired, setIsRecaptchaRequired] = useState(false);

  // hook to handle cloudflare /challenge api call
  useEffect(() => {
    const performCloudflareBotCheck = async () => {
      setIsBotCheckStarted(true);
      handleCloudflareBotCheck(
        isChallengedByCloudFlare(analytics, flow),
        setFormState,
        formSubmitted,
        formLoading,
        setBotState,
        setIsRecaptchaRequired,
      );
    };

    if (!isBotCheckStarted) {
      performCloudflareBotCheck();
    }
  }, [
    analytics,
    setFormState,
    setBotState,
    setIsRecaptchaRequired,
    formSubmitted,
    formLoading,
    isBotCheckStarted,
    flow,
  ]);

  const isRecaptchaRequiredError =
    formError === recaptchaNonResetErrors.recaptcha_required;

  // hook to handle rendering recaptcha
  useEffect(() => {
    const performRecaptchaRender = async () => {
      setBotState({ isBotCheckDone, isRenderRecaptchaStarted: true });
      handleRecaptchaRender(
        renderCaptchaCheckbox(analytics, flow, googleCaptchaSiteKey!),
        setFormState,
        formSubmitted,
        formLoading,
        setBotState,
        isBotCheckDone,
      );
    };

    if (
      shouldRenderRecaptcha(
        recaptchaScriptLoaded,
        isRecaptchaRequired || isRecaptchaRequiredError,
        isRenderRecaptchaStarted,
      )
    ) {
      performRecaptchaRender();
    }
  }, [
    isRecaptchaRequired,
    isRecaptchaRequiredError,
    googleCaptchaSiteKey,
    analytics,
    setFormState,
    formSubmitted,
    formLoading,
    setBotState,
    isRenderRecaptchaStarted,
    isBotCheckDone,
    recaptchaScriptLoaded,
    flow,
  ]);

  // hook to handle resetting recaptcha
  useEffect(() => {
    if (
      formError &&
      !isNonResetRecaptchaError(formError) &&
      isRenderRecaptchaDone
    ) {
      resetRecaptcha(flow, googleCaptchaSiteKey!);
    }
  }, [formError, isRenderRecaptchaDone, googleCaptchaSiteKey, flow]);

  useEffect(() => {
    if (showRecaptcha && isRenderRecaptchaDone) {
      resetRecaptcha(flow, googleCaptchaSiteKey!);
    }
  }, [flow, isRenderRecaptchaDone, showRecaptcha, googleCaptchaSiteKey]);

  return <div />;
};

export default ReCaptcha;
